import { createAsyncThunk } from "@reduxjs/toolkit"
import { type NavigateFunction } from "react-router/dist/lib/hooks"

import { RoutesPaths } from "../../../constants/routes.ts"
import { errorSave } from "../../../redux/errors.slice"
import { type AppDispatchWithRouter, type RootState } from "../../../store.ts"
import type { BasePagination } from "../../../types/api.ts"
import { handleGetResponseData } from "../../../utils/handlers/handle-get-response-data"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import { showNotification } from "../../../utils/notification/show-notification"
import { bulkUpdatePhases } from "../services/phases.services"
import * as services from "../services/project-types.services"
import type { ProjectTypeListParams } from "../services/services.types.ts"
import type {
  ProjectTypeCopyParams,
  ProjectTypeTemplate,
  UpdateProjectTypeAndPhaseParams
} from "../types/project-type.types.ts"
import { setTemplatesProp } from "./templates.slice.ts"

export const fetchProjectTypes = createAsyncThunk<
  BasePagination<ProjectTypeTemplate>,
  (ProjectTypeListParams & { isMore?: boolean }) | void
>(
  "projectTypes/fetchProjectTypes",
  async (params, { dispatch, getState, rejectWithValue }) => {
    const filters = (getState() as RootState)?.templates?.projectTypes?.filters
    // `isMore` tells the reducer whether to append; it is not a query parameter.
    const { isMore: _isMore, ...pageParams } = params || {}

    const response = await services.getProjectTypes({
      ...filters,
      ...pageParams
    })

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data as BasePagination<ProjectTypeTemplate>
  }
)

export const createProjectType = createAsyncThunk(
  "projectTypes/createProjectType",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.createProjectType(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    dispatch(fetchProjectTypes({ page_size: 100 }))
    showNotification.success({ message: "Project type created successfully." })

    return response.data
  }
)

export const fetchProjectTypeDetails = createAsyncThunk<
  ProjectTypeTemplate,
  string
>(
  "projectTypes/fetchProjectTypeDetails",
  async (projectTypeId, { dispatch, rejectWithValue }) => {
    const response = await services.getProjectTypeDetails(projectTypeId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data as ProjectTypeTemplate
  }
)

export const updateProjectType = createAsyncThunk(
  "projectTypes/updateProjectType",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.updateProjectType(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    dispatch(fetchProjectTypes({}))
    dispatch(setTemplatesProp({ key: `projectTypeDetails`, value: null }))
    showNotification.success({ message: "Project type updated successfully." })

    return response.data
  }
)

export const partialUpdateProjectType = createAsyncThunk(
  "projectTypes/partialUpdateProjectType",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.partialUpdateProjectType(params)
    const data = handleGetResponseData(response)?.data

    if (!response.ok) {
      dispatch(errorSave(data))
      return rejectWithValue(data)
    }

    showResponseMessage(response, "Project type updated successfully.")

    return data?.data
  }
)

export const deleteProjectType = createAsyncThunk<
  any,
  { id: string; navigate: NavigateFunction },
  { dispatch: AppDispatchWithRouter }
>(
  "projectTypes/deleteProjectType",
  async ({ id, navigate }, { dispatch, rejectWithValue }) => {
    const response = await services.deleteProjectType(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    navigate(RoutesPaths.settings.projectTemplates)

    dispatch(fetchProjectTypes({ page_size: 100 }))
    dispatch(setTemplatesProp({ key: "projectTypeDetails", value: null }))
    showNotification.success({ message: "Project type deleted successfully." })

    return response.data
  }
)

export const copyProjectType = createAsyncThunk<any, ProjectTypeCopyParams>(
  "projectTypes/copyProjectType",
  async (params, { dispatch, rejectWithValue }) => {
    const { id, name } = params
    const payload = name ? { name } : {}
    const response = await services.copyProjectType(id, payload)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    const copiedProjectType = response?.data?.details?.data
    const projectTypeName = name || copiedProjectType?.name || "Template"

    showResponseMessage(response, `${projectTypeName} copied successfully.`)

    return response.data
  }
)

export const resetProjectTypeToDefault = createAsyncThunk<
  ProjectTypeTemplate,
  { id: string }
>(
  "projectTypes/resetProjectTypeToDefault",
  async ({ id }, { dispatch, rejectWithValue }) => {
    const response = await services.resetProjectTypeToDefault(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    const reset = response?.data?.details?.data as ProjectTypeTemplate
    showResponseMessage(
      response,
      `${reset?.name || "Template"} is back to its default.`
    )

    return reset
  }
)

export const autocompleteProjectTypes = createAsyncThunk(
  "projectTypes/autocompleteProjectTypes",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.autocompleteProjectTypes(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data
  }
)

export const updateProjectTypeAndPhase = createAsyncThunk<
  ProjectTypeTemplate,
  UpdateProjectTypeAndPhaseParams
>(
  "projectTypes/updateProjectTypeAndPhase",
  async ({ projectType, phase }, { dispatch, rejectWithValue }) => {
    const { phases: projectTypePhases, ...projectTypeRest } = projectType
    let phases = projectTypePhases
    const response = await services.partialUpdateProjectType(projectTypeRest)
    const data = handleGetResponseData(response)?.data

    if (!response.ok) {
      dispatch(errorSave(data))
      return rejectWithValue(data)
    }

    const updatedProjectType = response.data?.details
      ?.data as ProjectTypeTemplate

    if (phase?.id) {
      phases = phases?.map((p) => (p.id === phase.id ? { ...p, ...phase } : p))
    }

    if (phases?.length) {
      const buildResponse = await bulkUpdatePhases({ phases })

      if (!buildResponse.ok) {
        dispatch(errorSave(buildResponse?.data?.details))
        return rejectWithValue(buildResponse.data)
      }

      updatedProjectType.phases = buildResponse.data?.details
        ?.data as ProjectTypeTemplate["phases"]
    }

    showResponseMessage({}, "Project type and milestone updated successfully.")

    return updatedProjectType
  }
)
